import { CloseOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { modalStyle } from '@/styles/style';

import type { KustomerInfo } from '../types';
import { IMPORT_STATE } from '../types';

type Props = {
  onConnectComplete: (workspace: KustomerInfo) => void;
};

const KustomerConnectModal = (props: Props) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const color = useColor();
  const { closeModal } = useModalContext();

  const [subDomain, setSubDomain] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  const onConnectClick = async () => {
    if (subDomain.trim() === '') return;
    if (!currWorkspace) return;
    const data = await service.kustomer.createKustomerInfo(
      currWorkspace.alias,
      subDomain,
      token,
    );
    if (data) {
      props.onConnectComplete({
        workspace_id: data.workspace_id || data.alias,
        alias: data.alias,
        domain: data.domain || subDomain,
        import_state:
          data.import_state || data.state || IMPORT_STATE.NOT_STARTED,
        history: data.history || [],
      });
      closeModal();
    } else {
      setError('Your token is invalid. Please check again!');
    }
  };
  return (
    <ModalStyle d="column">
      <Flex j="space-between">
        <Heading type="h2">Kustomer Integration</Heading>
        <IconButton
          icon={<CloseOutlined />}
          outlined={false}
          sizeType="l"
          onClick={closeModal}
        />
      </Flex>
      <Space h="24px" />
      <Typo type="label1" weight="m">
        Subdomain
      </Typo>
      <Space h="4px" />
      <Typo type="label2" color={color.gray700}>
        Enter the subdomain of your Kustomer URL.
      </Typo>
      <Space h="4px" />
      <Typo type="body2" color={color.gray700}>
        (ex. https://<b>your-subdomain</b>.kustomer.com)
      </Typo>
      <Space h="16px" />
      <Input
        placeholder="Subdomain"
        sizeType="m"
        value={subDomain}
        onChange={(e) => setSubDomain(e.target.value)}
      />
      <Space h="24px" />
      <Typo type="label1" weight="m">
        Access token
      </Typo>
      <Space h="4px" />
      <Typo type="label2" color={color.gray700}>
        Enter the access token of kustomer.
      </Typo>
      <Space h="16px" />
      <Input
        placeholder="Access token"
        sizeType="m"
        value={token}
        onChange={(e) => setToken(e.target.value)}
      />
      <Space h="32px" />
      {error ? (
        <Flex j="space-between">
          <Typo type="label2" style={{ color: color.red600 }}>
            {error}
          </Typo>
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={subDomain.trim() === ''}
          >
            Connect
          </Button>
        </Flex>
      ) : (
        <Flex j="flex-end">
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={subDomain.trim() === '' || token.trim() === ''}
          >
            Connect
          </Button>
        </Flex>
      )}
    </ModalStyle>
  );
};

const ModalStyle = styled(Flex)`
  width: 620px;
  padding: 32px;
  color: ${(props) => props.theme.colors.gray800};
  ${modalStyle};
`;

export default KustomerConnectModal;
